// E-Editions page (2026-10-27; left rail since 10-28). Upload an edition for any paper, configure each paper's
// scrollable (ecore) and FlipHTML5 flipbook (flipcore) workflows, add new papers, and see every
// run with its steps. Clients only see their own papers on the portal. Backend: src/eedition.js.
import { useState, useEffect, useCallback } from "react";
import API from "../api";
import { Newspaper, Plus, Pencil, X, Check, AlertTriangle, FolderSearch, Upload } from "lucide-react";
import { ConvoStyles } from "./conversation/Conversation";
import EEditionUpload, { EditionList } from "./EEditionUpload";

const panelStyle = { background: "var(--t-gloss, none), var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius: "var(--t-radius-lg, 16px)", boxShadow: "var(--t-shadow-panel)", padding: 20, marginBottom: 16 };
const lbl = { display: "block", fontSize: 12, fontWeight: 700, color: "var(--t-text-label)", margin: "0 0 5px" };
const inp = { width: "100%", height: 36, padding: "0 10px", fontSize: 13.5, borderRadius: "var(--t-radius-sm, 8px)" };
const PAL = { text: "var(--t-text-primary)", muted: "var(--t-text-muted)", faint: "var(--t-text-faint)", border: "var(--t-border-soft)", input: "var(--t-input)",
  accent: "var(--t-accent)", hover: "var(--t-accent-softer)", ok: "var(--badge-green)", danger: "var(--badge-red)" };
const EMPTY = { name: "", slug: "", prefix: "", client_id: "", owner_label: "", is_active: true, portal_enabled: false, portal_domains: "",
  scroll_enabled: true, ecore_path: "/home/site1/public_html/ecore/", latest_width: 316, latest_quality: 90, thumb_width: 300, thumb_quality: 30,
  manifest_enabled: false, manifest_path: "", manifest_name: "{prefix}{date}.xml",
  flip_enabled: false, flip_path: "/home/site1/public_html/flipcore/", flip_prefix: "", flip_profile_name: "", flip_caption: "", flip_profile_raw: "" };

function Field({ label, children, wide }) {
  return <label style={{ display: "block", gridColumn: wide ? "1 / -1" : undefined }}><span style={lbl}>{label}</span>{children}</label>;
}
function Toggle({ checked, onChange, children }) {
  return (
    <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13.5, color: "var(--t-text-primary)", cursor: "pointer", fontWeight: 600 }}>
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} /> {children}
    </label>
  );
}

function EditPub({ pub, clients, onClose, onSaved }) {
  const isNew = !pub.id;
  const [f, setF] = useState(() => ({ ...EMPTY, ...Object.fromEntries(Object.entries(pub).map(([k, v]) => [k, Array.isArray(v) ? v.join(", ") : v ?? (typeof EMPTY[k] === "number" ? EMPTY[k] : "")])) }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [folders, setFolders] = useState(null);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setBusy(true); setError("");
    const body = {};
    for (const k of Object.keys(EMPTY)) {
      let v = f[k];
      if (["latest_width", "latest_quality", "thumb_width", "thumb_quality"].includes(k)) v = Number(v);
      else if (typeof EMPTY[k] === "string") v = v === "" ? null : v;
      body[k] = v;
    }
    if (!isNew) delete body.slug;
    try {
      const r = isNew ? await API.post("/admin/eeditions/publications", body) : await API.patch(`/admin/eeditions/publications/${pub.id}`, body);
      onSaved(r.data);
    } catch (e) { setError(e?.response?.data?.error || "Couldn't save."); }
    finally { setBusy(false); }
  };
  const checkFolders = async () => {
    setFolders({ busy: true });
    try { setFolders((await API.post(`/admin/eeditions/publications/${pub.id}/check-folders`)).data); }
    catch (e) { setFolders({ error: e?.response?.data?.error || "Couldn't reach site1." }); }
  };
  const loadProfile = (file) => { if (!file) return; const rd = new FileReader(); rd.onload = () => { set("flip_profile_raw", String(rd.result)); if (!f.flip_profile_name) set("flip_profile_name", file.name.replace(/\.txt$/i, "")); }; rd.readAsText(file); };
  const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 14 };
  const sect = { fontSize: 13, fontWeight: 800, letterSpacing: ".04em", textTransform: "uppercase", color: "var(--t-text-faint)", margin: "6px 0 10px" };
  const mark = (ok) => ok === undefined ? null : ok ? <span style={{ color: "var(--badge-green)" }}> ✓ on site1</span> : <span style={{ color: "var(--badge-red)" }}> ✗ not on site1</span>;
  return (
    <div role="dialog" aria-modal="true" style={{ position: "fixed", inset: 0, zIndex: 400, background: "rgba(2,6,23,.6)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 16px", overflowY: "auto" }} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...panelStyle, width: "100%", maxWidth: 860, marginBottom: 40 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <h2 style={{ fontSize: 17, margin: 0 }}>{isNew ? "Add a publication" : `Edit ${pub.name}`}</h2>
          <div style={{ flex: 1 }} />
          <button type="button" className="cv-btn cv-btn-icon cv-btn-ghost" aria-label="Close" onClick={onClose}><X size={16} /></button>
        </div>

        <div style={sect}>Paper</div>
        <div style={grid}>
          <Field label="Name"><input className="cv-input" style={inp} value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Times Leader" /></Field>
          <Field label="Folder name (its storage folder here)"><input className="cv-input" style={inp} value={f.slug} disabled={!isNew} onChange={(e) => set("slug", e.target.value.toLowerCase())} placeholder="timesleader" /></Field>
          <Field label="File prefix"><input className="cv-input" style={inp} value={f.prefix} onChange={(e) => set("prefix", e.target.value)} placeholder="TIM" /></Field>
          <Field label="Client (who sees it on the portal)">
            <select className="cv-input" style={inp} value={f.client_id || ""} onChange={(e) => set("client_id", e.target.value)}>
              <option value="">— none yet —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          </Field>
          {!f.client_id && <Field label="Owner (until the client exists)"><input className="cv-input" style={inp} value={f.owner_label || ""} onChange={(e) => set("owner_label", e.target.value)} placeholder="PAW" /></Field>}
        </div>
        <div style={{ display: "flex", gap: 22, flexWrap: "wrap", marginBottom: 18 }}>
          <Toggle checked={f.is_active} onChange={(v) => set("is_active", v)}>Active</Toggle>
          <Toggle checked={f.portal_enabled} onChange={(v) => set("portal_enabled", v)}>On the client portal</Toggle>
        </div>
        {f.portal_enabled && (
          <div style={{ ...grid, marginTop: -6 }}>
            <Field label="Only people with these email domains (blank = everyone at the client)" wide>
              <input className="cv-input" style={inp} value={f.portal_domains || ""} onChange={(e) => set("portal_domains", e.target.value)} placeholder="trftimes.com" />
            </Field>
          </div>
        )}

        <div style={sect}>Scrollable e-edition (ecore)</div>
        <div style={{ marginBottom: 10 }}><Toggle checked={f.scroll_enabled} onChange={(v) => set("scroll_enabled", v)}>Send the edition PDF + pictures to site1</Toggle></div>
        <div style={grid}>
          <Field label={<>ecore folder on site1{mark(folders?.ecore)}</>} wide><input className="cv-input" style={inp} value={f.ecore_path || ""} onChange={(e) => set("ecore_path", e.target.value)} /></Field>
          <Field label="latest.jpg width (px)"><input className="cv-input" style={inp} type="number" value={f.latest_width} onChange={(e) => set("latest_width", e.target.value)} /></Field>
          <Field label="latest.jpg quality"><input className="cv-input" style={inp} type="number" value={f.latest_quality} onChange={(e) => set("latest_quality", e.target.value)} /></Field>
          <Field label="…final.jpg width (px)"><input className="cv-input" style={inp} type="number" value={f.thumb_width} onChange={(e) => set("thumb_width", e.target.value)} /></Field>
          <Field label="…final.jpg quality"><input className="cv-input" style={inp} type="number" value={f.thumb_quality} onChange={(e) => set("thumb_quality", e.target.value)} /></Field>
        </div>
        <div style={{ marginBottom: 10 }}><Toggle checked={f.manifest_enabled} onChange={(v) => set("manifest_enabled", v)}>Also send the page list (XML)</Toggle></div>
        {f.manifest_enabled && (
          <div style={grid}>
            <Field label={<>Page-list folder on site1{mark(folders?.manifest)}</>} wide><input className="cv-input" style={inp} value={f.manifest_path || ""} onChange={(e) => set("manifest_path", e.target.value)} /></Field>
            <Field label="Page-list file name ({prefix}, {date} = MMDDYY)"><input className="cv-input" style={inp} value={f.manifest_name || ""} onChange={(e) => set("manifest_name", e.target.value)} /></Field>
          </div>
        )}

        <div style={sect}>Flipbook (FlipHTML5 → flipcore)</div>
        <div style={{ marginBottom: 10 }}><Toggle checked={f.flip_enabled} onChange={(v) => set("flip_enabled", v)}>Build a FlipHTML5 flipbook and send it to site1</Toggle></div>
        {f.flip_enabled && (
          <>
            <div style={grid}>
              <Field label={<>flipcore folder on site1{mark(folders?.flipcore)}</>} wide><input className="cv-input" style={inp} value={f.flip_path || ""} onChange={(e) => set("flip_path", e.target.value)} /></Field>
              <Field label="FlipHTML5 profile name (-p)"><input className="cv-input" style={inp} value={f.flip_profile_name || ""} onChange={(e) => set("flip_profile_name", e.target.value)} placeholder="flipTL" /></Field>
              <Field label="Book caption (-c)"><input className="cv-input" style={inp} value={f.flip_caption || ""} onChange={(e) => set("flip_caption", e.target.value)} placeholder={f.name} /></Field>
              <Field label="Flipbook prefix (blank = file prefix)"><input className="cv-input" style={inp} value={f.flip_prefix || ""} onChange={(e) => set("flip_prefix", e.target.value)} /></Field>
            </div>
            <Field label="Profile file" wide>
              <textarea className="cv-input" rows={8} value={f.flip_profile_raw || ""} onChange={(e) => set("flip_profile_raw", e.target.value)}
                style={{ width: "100%", padding: "8px 10px", fontSize: 12, fontFamily: "ui-monospace, Menlo, monospace", resize: "vertical", borderRadius: "var(--t-radius-sm, 8px)" }} />
            </Field>
            <label className="cv-btn" style={{ marginTop: 8, display: "inline-flex" }}><Upload size={14} /> Load a profile .txt
              <input type="file" accept=".txt,text/plain" style={{ display: "none" }} onChange={(e) => { loadProfile(e.target.files?.[0]); e.target.value = ""; }} />
            </label>
          </>
        )}

        {folders?.error && <div style={{ fontSize: 12.5, color: "var(--badge-red)", marginTop: 12 }}>{folders.error}</div>}
        {error && <div role="alert" style={{ fontSize: 13, color: "var(--badge-red)", marginTop: 14 }}>{error}</div>}
        <div style={{ display: "flex", gap: 10, marginTop: 18, flexWrap: "wrap" }}>
          <button type="button" className="cv-btn cv-btn-primary" disabled={busy} onClick={save}><Check size={15} /> {busy ? "Saving…" : "Save"}</button>
          {!isNew && <button type="button" className="cv-btn" disabled={folders?.busy} onClick={checkFolders}><FolderSearch size={15} /> {folders?.busy ? "Checking…" : "Check folders on site1"}</button>}
          <button type="button" className="cv-btn cv-btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// The FlipHTML5 program: a zip of C:\Program Files (x86)\Flip HTML5 from the PC that runs it, installed on the server.
function FlipHtml5Program({ installed, onDone }) {
  const [st, setSt] = useState(null);   // { pct } uploading · { installing } · { error } · { done }
  const send = async (file) => {
    if (!file) return;
    if (!/\.zip$/i.test(file.name)) { setSt({ error: "Pick the .zip of the Flip HTML5 folder." }); return; }
    setSt({ pct: 0 });
    const fd = new FormData(); fd.append("file", file, file.name);
    try {
      const r = await API.post("/admin/eeditions/fliphtml5", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 0,
        onUploadProgress: (ev) => setSt(ev.total && ev.loaded < ev.total ? { pct: Math.round((ev.loaded / ev.total) * 100) } : { installing: true }),
      });
      setSt({ done: r.data }); onDone();
    } catch (e) { setSt({ error: e?.response?.data?.error || "The upload didn't finish — try again." }); }
  };
  const busy = !!st && (st.pct !== undefined || st.installing);
  return (
    <div style={panelStyle}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
        <h2 style={{ fontSize: 15, margin: 0 }}>FlipHTML5 program</h2>
        {installed ? <span style={{ fontSize: 12.5, color: "var(--badge-green)", fontWeight: 700 }}><Check size={13} /> Installed on the server</span>
          : <span style={{ fontSize: 12.5, color: "var(--badge-amber)", fontWeight: 700 }}>Not installed</span>}
        <div style={{ flex: 1 }} />
        <label className={`cv-btn${installed ? "" : " cv-btn-primary"}`} style={{ display: "inline-flex", opacity: busy ? 0.6 : 1, pointerEvents: busy ? "none" : undefined }}>
          <Upload size={14} /> {installed ? "Upload a new copy (.zip)" : "Upload the Flip HTML5 folder (.zip)"}
          <input type="file" accept=".zip,application/zip,application/x-zip-compressed" style={{ display: "none" }} disabled={busy}
            onChange={(e) => { send(e.target.files?.[0]); e.target.value = ""; }} />
        </label>
      </div>
      <div style={{ fontSize: 12.5, color: "var(--t-text-faint)" }}>
        On the PC that runs FlipHTML5, right-click <b>C:\Program Files (x86)\Flip HTML5</b> → Send to → Compressed (zipped) folder, then upload that zip.
      </div>
      {st?.pct !== undefined && <div style={{ fontSize: 13, marginTop: 10, color: "var(--t-text-primary)" }}>Uploading… {st.pct}%</div>}
      {st?.installing && <div style={{ fontSize: 13, marginTop: 10, color: "var(--t-text-primary)" }}>Uploaded — installing it on the server…</div>}
      {st?.error && <div role="alert" style={{ fontSize: 13, marginTop: 10, color: "var(--badge-red)" }}>{st.error}</div>}
      {st?.done && (
        <div style={{ fontSize: 13, marginTop: 10, color: st.done.launches ? "var(--badge-green)" : "var(--badge-amber)" }}>
          Installed {st.done.files.toLocaleString()} files.{" "}
          {st.done.launches ? "FlipHTML5 starts on the server." : "But FlipHTML5 didn't start on the server, so flipbooks will fail until that's sorted."}
        </div>
      )}
    </div>
  );
}

export default function EEditionsPanel() {
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => {
    API.get("/admin/eeditions").then((r) => setData(r.data)).catch((e) => setError(e?.response?.data?.error || "Couldn't load e-editions."));
  }, []);
  useEffect(() => { load(); }, [load]);
  const active = (data?.runs || []).some((r) => ["queued", "running"].includes(r.status));
  useEffect(() => { if (!active) return undefined; const t = setInterval(load, 5000); return () => clearInterval(t); }, [active, load]);
  const retry = async (r) => { try { await API.post(`/admin/eeditions/runs/${r.id}/retry`, {}); load(); } catch (e) { window.alert(e?.response?.data?.error || "Couldn't run it again."); } };

  if (error) return <div style={{ ...panelStyle, color: "var(--badge-red)" }}>{error}</div>;
  if (!data) return <div style={{ ...panelStyle, color: "var(--t-text-faint)" }}>Loading…</div>;
  const pubs = data.publications;
  const owner = (p) => p.client_name || p.owner_label || "—";
  const sorted = [...pubs].sort((a, b) => owner(a).localeCompare(owner(b)) || a.name.localeCompare(b.name));
  const td = { padding: "9px 10px", fontSize: 13, borderBottom: "1px solid var(--t-border-softer)", verticalAlign: "top" };
  const yes = (on, text) => on ? <span style={{ color: "var(--t-text-primary)" }}>{text}</span> : <span style={{ color: "var(--t-text-faint)" }}>off</span>;
  return (
    <div>
      <ConvoStyles />
      {(!data.ftp_configured || !data.fliphtml5_installed) && (
        <div style={{ ...panelStyle, borderLeft: "4px solid var(--badge-amber)", display: "flex", gap: 10, alignItems: "flex-start", padding: "12px 16px" }}>
          <AlertTriangle size={17} color="var(--badge-amber)" style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 13.5, color: "var(--t-text-primary)" }}>
            {!data.ftp_configured && <div>The site1 FTP login isn't set on the server, so nothing can be sent.</div>}
            {!data.fliphtml5_installed && <div>FlipHTML5 isn't installed on the server yet — flipbook papers will stop at the flipbook step until it is. Upload it under “FlipHTML5 program” below.</div>}
          </div>
        </div>
      )}

      <div style={panelStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <Newspaper size={17} color="var(--t-accent)" /><h2 style={{ fontSize: 15, margin: 0 }}>Upload an edition</h2>
        </div>
        <EEditionUpload client={API} base="/admin/eeditions" publications={pubs.filter((p) => p.is_active)} today={data.today} pal={PAL} onDone={load} />
      </div>

      <div style={panelStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <h2 style={{ fontSize: 15, margin: 0 }}>Publications</h2>
          <span style={{ fontSize: 12.5, color: "var(--t-text-faint)" }}>{pubs.length}</span>
          <div style={{ flex: 1 }} />
          <button type="button" className="cv-btn cv-btn-primary" onClick={() => setEdit({})}><Plus size={15} /> Add publication</button>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 860 }}>
            <thead><tr>{["Paper", "Client", "Prefix", "E-edition (ecore)", "Flipbook", "Portal", ""].map((h) => (
              <th key={h} style={{ textAlign: "left", fontSize: 10.5, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--t-text-faint)", padding: "8px 10px", borderBottom: "1px solid var(--t-border-softer)" }}>{h}</th>))}</tr></thead>
            <tbody>
              {sorted.map((p) => (
                <tr key={p.id} style={{ opacity: p.is_active ? 1 : 0.55 }}>
                  <td style={{ ...td, fontWeight: 700, color: "var(--t-text-primary)" }}>{p.name}<div style={{ fontWeight: 400, fontSize: 11.5, color: "var(--t-text-faint)" }}>{p.slug}</div></td>
                  <td style={td}>{owner(p)}</td>
                  <td style={{ ...td, fontFamily: "ui-monospace, Menlo, monospace" }}>{p.prefix}</td>
                  <td style={td}>{yes(p.scroll_enabled, <>on · {p.latest_width}/{p.thumb_width} px{p.manifest_enabled ? " · page list" : ""}</>)}</td>
                  <td style={td}>{yes(p.flip_enabled, <>{p.flip_profile_name || "no profile"}</>)}</td>
                  <td style={td}>{p.portal_enabled ? <><Check size={15} color="var(--badge-green)" />{(p.portal_domains || []).length ? <div style={{ fontSize: 11.5, color: "var(--t-text-faint)" }}>only {p.portal_domains.join(", ")}</div> : null}</> : <span style={{ color: "var(--t-text-faint)" }}>off</span>}</td>
                  <td style={{ ...td, textAlign: "right" }}><button type="button" className="cv-btn" onClick={() => setEdit(p)}><Pencil size={13} /> Edit</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <FlipHtml5Program installed={data.fliphtml5_installed} onDone={load} />

      <div style={panelStyle}>
        <h2 style={{ fontSize: 15, margin: "0 0 10px" }}>Recent editions</h2>
        <EditionList runs={data.runs} pal={PAL} onRetry={retry} showError />
      </div>

      {edit && <EditPub pub={edit} clients={data.clients} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}
